import { Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Icon, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { avisarSolicitacoesAlteradas, enviarComAnexos } from "views/umc/filaSolicitacoes/solicitacaoUtils";

/**
 * Devolução direta (quem tem permissão): primeiro a pergunta "deseja devolver?"; depois a tela para escrever um texto
 * e anexar documentos, que ficam junto da ocorrência de devolução (o texto no histórico, o arquivo na aba Documentos
 * ligado a ela). A devolução só é feita ao concluir a segunda tela, com texto e anexos na mesma chamada.
 *
 * alvo: { titulo, pergunta, detalhe (opcional), endpoint (ex.: /devedor/devolver/{id}) } ou null.
 * onConcluida(aviso): chamado depois da devolução (aviso vazio; mantido para quem já concatena na mensagem).
 */
export default function DevolucaoDiretaDialog({ alvo, onClose, onConcluida }) {
    const inputRef = useRef(null);
    const [etapa, setEtapa] = useState("pergunta");
    const [observacao, setObservacao] = useState("");
    const [arquivos, setArquivos] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState("");

    useEffect(() => {
        if (alvo) {
            setEtapa("pergunta");
            setObservacao("");
            setArquivos([]);
            setErro("");
        }
    }, [alvo]);

    const fechar = () => {
        if (!enviando) onClose();
    };

    const escolherArquivos = (e) => {
        const novos = Array.from(e.target.files || []);
        setArquivos(atual => [...atual, ...novos.filter(n => !atual.some(a => a.name === n.name && a.size === n.size))]);
        e.target.value = "";
    };

    const concluir = async () => {
        setEnviando(true);
        setErro("");
        try {
            // Devolução, texto e anexos numa chamada só: se um arquivo não puder ser gravado, nada é devolvido
            await enviarComAnexos(alvo.endpoint, { observacao: observacao.trim() || null }, arquivos);
            avisarSolicitacoesAlteradas();
            onConcluida("");
            onClose();
        } catch (err) {
            setErro(err.message || "Erro ao devolver.");
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Dialog open={!!alvo} onClose={fechar} maxWidth={etapa === "pergunta" ? "xs" : "sm"} fullWidth>
            {alvo && etapa === "pergunta" && (
                <>
                    <DialogTitle>{alvo.titulo}</DialogTitle>
                    <DialogContent>
                        <MDTypography variant="body2" color="text">{alvo.pergunta}</MDTypography>
                        {alvo.detalhe && (
                            <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1.5 }}>{alvo.detalhe}</MDTypography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <MDButton variant="text" color="dark" onClick={fechar}>Não</MDButton>
                        <MDButton variant="gradient" color="error" onClick={() => setEtapa("complemento")}>Sim, devolver</MDButton>
                    </DialogActions>
                </>
            )}

            {alvo && etapa === "complemento" && (
                <>
                    <DialogTitle>{alvo.titulo}: complemento da ocorrência</DialogTitle>
                    <DialogContent dividers>
                        <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 2 }}>
                            Escreva o motivo ou observação e anexe o documento da devolução, se houver. O texto vai na ocorrência de
                            devolução da ficha e o arquivo fica na aba Documentos (tipo Devolução), junto dessa ocorrência. Os dois são opcionais.
                        </MDTypography>
                        <TextField fullWidth multiline rows={4} variant="outlined" autoFocus label="Observação da devolução"
                            value={observacao} onChange={(e) => setObservacao(e.target.value)} disabled={enviando} />

                        <MDBox mt={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <MDButton variant="outlined" color="info" size="small" onClick={() => inputRef.current && inputRef.current.click()} disabled={enviando}>
                                <Icon sx={{ mr: 0.5 }}>attach_file</Icon>{arquivos.length ? "Adicionar outro documento" : "Anexar documento"}
                            </MDButton>
                            {arquivos.map(a => (
                                <Chip key={`${a.name}-${a.size}`} size="small" label={a.name} variant="outlined"
                                    onDelete={enviando ? undefined : () => setArquivos(atual => atual.filter(x => x !== a))} />
                            ))}
                            <input ref={inputRef} type="file" multiple hidden onChange={escolherArquivos} />
                        </MDBox>

                        {erro && (
                            <MDTypography variant="caption" color="error" fontWeight="bold" sx={{ display: "block", mt: 2 }}>{erro}</MDTypography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <MDButton variant="text" color="dark" onClick={fechar} disabled={enviando}>Cancelar</MDButton>
                        <MDButton variant="gradient" color="error" onClick={concluir} disabled={enviando}>
                            {enviando ? <CircularProgress size={18} color="inherit" /> : "Concluir devolução"}
                        </MDButton>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}

DevolucaoDiretaDialog.propTypes = {
    alvo: PropTypes.shape({
        titulo: PropTypes.string,
        pergunta: PropTypes.string,
        detalhe: PropTypes.string,
        endpoint: PropTypes.string,
        idDevedor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    onClose: PropTypes.func.isRequired,
    onConcluida: PropTypes.func.isRequired,
};
